import * as oidc from "oidc-client";
import { Environment, environmentSettings } from "./environments";

// Support for server side pre-rendering and testing
const memoryStorage = new oidc.InMemoryWebStorage();

oidc.Log.logger = console;
oidc.Log.level = oidc.Log.INFO;

export interface SessionManagerSettings {
  environment: Environment;
  clientId: string;
  scope: string;
  basePath?: string;
  automaticSilentRenew?: boolean;
  // Provide an implementation that uses either the loc parameter or the full path string parameter
  navigate: (loc: Location, fullPath: string) => void;
  // The following settings should generally not be used
  overrideUserManagerSettings?: oidc.UserManagerSettings;
}

export interface Location {
  pathname: string;
  hash: string;
  search: string;
  state: any;
}

export class SessionManager {
  private user: oidc.User | null = null;
  private loggedOutReason?: any;
  private userManager: oidc.UserManager;
  private eventListeners: (() => void)[] = [];
  private basePath = "";

  constructor(private settings: SessionManagerSettings) {
    this.basePath = settings.basePath || this.basePath;

    const defaultManagerSettings: oidc.UserManagerSettings = {
      redirect_uri: `${window.location.origin}${this.basePath}/#/signin-callback`,
      post_logout_redirect_uri: `${window.location.origin}${this.basePath}/#/logout-callback`,
      silent_redirect_uri: `${window.location.origin}${this.basePath}/silent-callback.html`,
      automaticSilentRenew: settings.automaticSilentRenew === true,
      response_type: "code",
      revokeAccessTokenOnSignout: true,
      scope: "openid",
      filterProtocolClaims: true,
      loadUserInfo: true,
      userStore: new oidc.WebStorageStateStore({
        store: window.sessionStorage ? sessionStorage : memoryStorage
      }),
      stateStore: new oidc.WebStorageStateStore({
        store: window.localStorage ? localStorage : memoryStorage
      })
    };

    this.userManager = new oidc.UserManager({
      ...defaultManagerSettings,
      authority: environmentSettings[settings.environment].url,
      client_id: settings.clientId,
      scope: settings.scope,
      monitorSession: false, // Disabled as it causes issues, mostly in safari
      ...(settings ? settings.overrideUserManagerSettings : {})
    });
  }

  public async initialize() {
    try {
      if (window.location.href.indexOf(this.userManager.settings.redirect_uri!) !== -1) {
        this.user = await this.userManager.signinRedirectCallback();
        this.navigate(this.user.state);
      } else if (
        window.location.href.indexOf(this.userManager.settings.post_logout_redirect_uri!) !== -1
      ) {
        const result = await this.userManager.signoutRedirectCallback();
        if (result.state) {
          this.loggedOutReason = result.state.reason;
          this.navigate(
            result.state.postLogoutLocation || {
              pathname: "/",
              hash: "",
              search: "",
              state: null
            }
          );
        }
      } else {
        const user = await this.userManager.getUser();
        if (user && !user.expired) {
          this.user = user;
        }
      }
    } catch (error) {
      // Need to clear URL to avoid user getting stuck bad application state
      this.navigate({ pathname: "/", hash: "", search: "", state: null });
      throw error;
    }

    this.userManager.events.addUserLoaded(this.handleUserChanged);
    this.userManager.events.addUserUnloaded(this.handleUserChanged);
    this.userManager.events.addAccessTokenExpired(this.handleAccessTokenExpired);
    this.userManager.events.addSilentRenewError(this.handleSilentRenewError);
  }

  private navigate(loc: Location) {
    this.settings.navigate(loc, loc.pathname + loc.search + loc.hash);
  }

  public getUser() {
    return this.user;
  }

  public getLoggedOutReason() {
    return this.loggedOutReason;
  }

  public async login(args?: any) {
    const loc: Location = {
      hash: window.location.hash,
      pathname: window.location.pathname.substr(
        this.basePath.length,
        window.location.pathname.length - this.basePath.length
      ),
      search: window.location.search,
      state: history.state
    };
    await this.userManager.signinRedirect({ ...args, state: loc });
  }

  public async logout(postLogoutLocation?: Location, reason?: any) {
    await this.userManager.signoutRedirect({ state: { postLogoutLocation, reason } });
  }

  public async dropUser() {
    await this.userManager.removeUser();
  }

  public addUserChangedListener(listener: () => void) {
    this.eventListeners.push(listener);
  }

  private handleUserChanged = async () => {
    const newUser = await this.userManager.getUser();

    if (newUser !== this.user) {
      this.user = newUser;
      this.eventListeners.forEach((x) => x());
    }
  };

  private handleSilentRenewError(...args: any[]) {
    console.error("SILENT RENEW ERROR: ", args);
  }

  private handleAccessTokenExpired = () => {
    this.userManager.removeUser();
  };
}
